import dayjs from 'dayjs';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { setItemList, setOrder, setRefresh } from 'application/store/orders';
import { handleShowOrder } from 'application/usecases/handle-show-order';

import { useShoppingCart } from 'hooks/useShoppingCart';
import { Container, PageTitle, Spinner } from 'view/components';
import { toMoney } from 'view/helpers';

import { handleDeleteAllOrders } from 'application/usecases/handle-delete-all-orders';
import { handleListConditions } from 'application/usecases/handle-list-conditions';
import { handleListFreighttypes } from 'application/usecases/handle-list-freight-types';
import { handleStoreOrder } from 'application/usecases/handle-store-item';
import { handleTransferOrders } from 'application/usecases/handle-transfer-orders';
import { useMemo } from 'react';
import { toast } from 'react-toastify';
import { ErrorMessage } from 'view/components/error-message';
import { MinValue, NotificationModal } from 'view/components/MinValue';
import { Item } from './item';
import { NotificationFreightType, NotificationOpenOrder } from './notifications';
import { endRequest } from './services';
import * as S from './styles';

export const ShowOrderPage = () => {
  const [deletingAllOrders, setDeletingAllOrders] = useState(false)
  const [loading, setLoading] = useState(true)
  const [informationOrderDialog, setInformationOrderDialog] = useState(false)
  const [freightTypes, setFreightTypes] = useState([])
  const [freightSelected, setFrengthSelected] = useState('')
  const [freightTypeObservation, setFreightTypeObservation] = useState('')
  const [conditions, setConditions] = useState([])
  const [conditionSelected, setConditionSelected] = useState('')
  const [conditionDialog, setConditionDialog] = useState(false)
  const [ending, setEnding] = useState(false)
  const [errors, setErrors] = useState([])
  const token = JSON.parse(localStorage.getItem('@barrosAuth'))
  const params = useParams()
  const navigate = useNavigate()

  const orders = useSelector((state) => state.orders)
  const banners = useSelector((state) => state.banners.banners)

  const dispatch = useDispatch()
  const { getAmount } = useShoppingCart()

  const [finalized, setFinalized] = useState(false)

  const [ordersTransfer, setOrdersTransfer] = useState([])
  const [transferingOrders, setTransferingOrders] = useState(false)

  const fetchOrder = useCallback(() => {
    handleShowOrder
      .handle({ Cpedido: params.id })
      .then((res) => {
        dispatch(setRefresh(false))
        dispatch(setOrder(res.data.Pedido))
        dispatch(setItemList(res.data.Pedido[0].Itens))
        setFinalized(res.data.Pedido[0].dFases !== 'Orçamento')

        if (res.data.Pedido[0]?.Informacao) setInformationOrderDialog(true)
      })
      .catch(() => {
        toast.error('Não foi possível listar os pedidos')
      })
  }, [params.id, dispatch])

  const fetchConditions = async () => {
    const response = await handleListConditions.handle({
      cCliente: Number(token?.clientsId)
    })
    const data = response?.data
    if (data?.nroRegistros > 0)
      setConditions(data?.Condicao?.sort((a, b) => a - b))
  }

  const fetchFreightTypes = async () => {
    const { data } = await handleListFreighttypes.handle({
      ClientesId: Number(token.clientsId),
      ValorPedido: orders.order[0]?.vPedTotal
    })

    setFreightTypes(data ?? [])
  }

  const handleTransferOrder = async () => {
    setTransferingOrders(true)
    try {
      const ordersToTransfer = orders.order
        .map((order) =>
          order.Itens?.map((item) => ({
            ...item,
            cPedOrigem: order.cPedido,
            cDescricao: order.cDescricao
          }))?.filter((item) => ordersTransfer.includes(item.dIndice))
        )
        ?.flat()

      await handleTransferOrders.handle(ordersToTransfer)

      setFinalized(false)
      setOrdersTransfer([])
      dispatch(setRefresh(true))

      // Reset shopping cart amount after successful order transfer
      await getAmount()

      fetchOrder()

      toast.success('Pedido transferido com sucesso')
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível transferir o pedido')
    } finally {
      setTransferingOrders(false)
    }
  }

  const handleChangeCondition = (e) => {
    const value = e.target.value
    if (!value) return setConditionSelected('')

    const condit = JSON.parse(value)
    const total = Number(orders.order[0]?.vPedTotal)
    setErrors([])

    setConditionSelected(condit)

    if (Number(condit.vValorMin) >= total) setConditionDialog(true)
  }

  const handleChangeFreightType = (e) => {
    const value = e.target.value
    setFrengthSelected(value)

    const freightType = freightTypes.find((freight) => freight.Codigo === value)

    setFreightTypeObservation(freightType?.Observacao ?? '')
  }

  useEffect(() => {
    setFinalized(false)
    dispatch(setOrder([]))
    fetchOrder()
  }, [fetchOrder, dispatch])

  useEffect(() => {
    if (orders.refresh) {
      setFinalized(false)
      dispatch(setOrder([]))
      fetchOrder()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders.refresh])

  useEffect(() => {
    setLoading(true)
    try {
      if (!conditions.length) {
        try {
          fetchConditions()
        } catch (error) {
          toast.error('Não foi possível listar as condições de pagamentos')
        }
      }

      if (!freightTypes.length && orders.order.length) {
        try {
          fetchFreightTypes()
        } catch (error) {
          toast.error('Não foi possível listar os tipos de frete')
        }
      }
    } catch (error) {
      toast.error(
        'Ocorreu um erro ao listar as condições de pagamentos e os tipos de frete'
      )
    } finally {
      setLoading(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditions.length, freightTypes.length, orders.order])

  async function handleEndRequest() {
    setErrors([])

    if (!conditionSelected) {
      if (!errors.filter((e) => e?.id === 'condition').length) {
        return setErrors([
          ...errors,
          {
            id: 'notHasCondition',
            Description: 'Selecione uma Condição de Pagamento'
          }
        ])
      }

      return
    }

    if (!freightSelected) {
      if (!errors.filter((e) => e?.id === 'freight').length) {
        return setErrors([
          ...errors,
          {
            id: 'notHasFreight',
            Description: 'Selecione um Tipo de Frete'
          }
        ])
      }

      return
    }

    // const quantityAndUnit = orders.itemList
    //   .map((item) => ({
    //     quantidade: item.vQuantidade,
    //     unitario: item.vUnitario
    //   }))
    //   .reduce((acc, cur) => ({
    //     quantidade: Number(acc.quantidade) + Number(cur.quantidade),
    //     unitario: Number(acc.unitario) + Number(cur.unitario)
    //   }))

    setEnding(true)

    try {
      const order = orders.order[0]

      for (const item of orders.itemList) {
        await handleStoreOrder.handle({
          cDescricao: order.cDescricao,
          cCliente: JSON.parse(localStorage.getItem('@barrosAuth'))?.clientsId,
          cItem: item.dItemCod,
          qtd: Number(item.vQuantidade),
          vUnit: (
            Number(item.vUnitario) +
            (Number(item.vUnitario) / 100) *
              (conditionSelected?.pPercentual ?? 0)
          ).toFixed(2),
          cCondicao: Number(conditionSelected.cPagtoId),
          cOperacao: 4
        })
      }

      await endRequest({
        Cpedido: params.id,
        cCondicao: Number(conditionSelected.cPagtoId),
        cTipoFrete: Number(freightSelected)
      }).then(async (res) => {
        if (res.data.Messages[0].Id) {
          if (res.data.Messages[0].Id !== '200') {
            setErrors(res.data.Messages)
            return res.data.Messages.map((message) =>
              toast.error(message.Description)
            )
          }
        }

        res.data.Messages.forEach((message) => {
          const successMessage = message.Description

          if (successMessage?.toLowerCase().includes('encerrado com sucesso'))
            return toast.success(message.Description)

          toast.warn(message.Description)
        })

        // Reset shopping cart amount after successful order finalization
        await getAmount()

        navigate('/pedidos')
      })
    } catch (err) {
      toast.error(err.message)
    } finally {
      setEnding(false)
    }
  }

  const handleDeleteAll = async () => {
    setDeletingAllOrders(true)

    await handleDeleteAllOrders
      .handle({
        cPedido: orders.order[0].cPedido
      })
      .then(async (res) => {
        if (res.data.Messages[0].Id) {
          if (res.data.Messages[0].Id !== '200') {
            setErrors(res.data.Messages)
            return res.data.Messages.map((message) =>
              toast.error(message.Description)
            )
          }
        }

        res.data.Messages.map((message) => toast.success(message.Description))

        // Reset shopping cart amount after successful order deletion
        await getAmount()

        navigate('/pedidos')
      })
      .catch((err) => {
        err?.Messages?.map((message) => toast.error(message?.Description)) ??
          toast.error('Não foi possível deletar')
      })
      .finally(() => setDeletingAllOrders(false))
  }

  const handleSelectAllOrders = (e) => {
    const value = e.target.checked

    if (value)
      setOrdersTransfer(
        orders.order
          ?.map((order) => order.Itens)
          ?.flat()
          .map((item) => item.dIndice)
      )
    else setOrdersTransfer([])
  }

  const handleSelectOrder = (e, indice) => {
    const value = e.target.checked

    if (!value)
      return setOrdersTransfer(
        ordersTransfer.filter((order) => order !== indice)
      )

    const item = orders.order
      ?.map((order) => order.Itens)
      ?.flat()
      .find((item) => item.dIndice === indice)

    if (value) setOrdersTransfer([...ordersTransfer, item.dIndice])
  }

  const totalAmount = useMemo(() => {
    return (
      orders?.itemList
        ?.map(
          (item) =>
            Number(item?.vTotItem) +
            (Number(item?.vTotItem) / 100) *
              (conditionSelected?.pPercentual ?? 0)
        )
        ?.reduce((acc, cur) => acc + cur, 0) ?? []
    )
  }, [conditionSelected?.pPercentual, orders])

  return (
    <>
      <NotificationModal show={conditionDialog}>
        <MinValue
          condition={conditionSelected ?? {}}
          onClose={() => setConditionDialog(false)}
        />
      </NotificationModal>

      <NotificationModal show={!!freightTypeObservation}>
        <NotificationFreightType
          handleClose={() => setFreightTypeObservation('')}
          observation={freightTypeObservation}
        />
      </NotificationModal>

      <NotificationModal show={informationOrderDialog}>
        <NotificationOpenOrder
          handleClose={() => setInformationOrderDialog(false)}
          information={orders?.Informacao}
        />
      </NotificationModal>

      <Container>
        <PageTitle>Pedido</PageTitle>

        {ending && (
          <div
            style={{
              position: 'fixed',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              backgroundColor: 'rgba(0,0,0,0.2)'
            }}
          >
            <Spinner absolute size="60" />
          </div>
        )}

        <S.CartContent>
          <S.CartProducts>
            <S.CartProductsTable>
              <thead>
                <tr>
                  <th colSpan={2} style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 16 }}>
                      <input type="checkbox" onChange={handleSelectAllOrders} />
                    </span>
                    Qtde
                  </th>
                  <th>Unitário</th>
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>

              {!loading &&
                orders.itemList &&
                orders.itemList.map((item) => (
                  <Item
                    key={item.dIndice}
                    item={item}
                    orderId={params.id}
                    promotionId={conditionSelected.cPagtoId}
                    promotionPercentage={conditionSelected.pPercentual ?? 0}
                    finalized={finalized}
                    minValue={conditionSelected?.vValorMin ?? 0}
                    handleSelectOrder={handleSelectOrder}
                    isSelected={ordersTransfer.includes(item.dIndice)}
                  />
                ))}
            </S.CartProductsTable>
          </S.CartProducts>
          <div
            className="custom-container"
            style={{ display: 'flex', flexDirection: 'column', maxWidth: 450 }}
          >
            <S.CartTotal>
              {!loading && (
                <S.CartTotalContent>
                  <S.CartTotalHeader>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'start',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div>
                        {orders.order.map((item) => (
                          <span
                            key={item.cPedido}
                            style={{
                              display: 'inline-block',
                              background: '#ff4d4f',
                              color: '#fff',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: 12,
                              fontWeight: 500,
                              marginRight: 6
                            }}
                          >
                            {item.cDescricao}
                          </span>
                        ))}
                      </div>
                      <p>
                        <strong>
                          Orçamento #
                          {orders.order.map((item) => item.cPedExterno)}
                        </strong>
                      </p>
                    </div>
                    <S.ButtonToDeleteOrder
                      disabled={deletingAllOrders}
                      onClick={handleDeleteAll}
                    >
                      {deletingAllOrders ? 'Apagando...' : 'Apagar pedido'}
                    </S.ButtonToDeleteOrder>
                  </S.CartTotalHeader>
                  <div>
                    <p>
                      <strong>Data abertura:</strong>
                    </p>
                    {orders.order.map((item) => (
                      <p key={item.cPedido}>
                        {dayjs(item.dtEmissao).format('DD/MM/YY')}
                      </p>
                    ))}
                  </div>
                  <hr />
                  <div>
                    <p>
                      <strong>Total:</strong>
                    </p>
                    {orders.order.map((item) => (
                      <p key={item.cPedido}>{toMoney(totalAmount)} </p>
                    ))}
                  </div>

                  <div
                    style={{
                      marginTop: 10,
                      width: '100%',
                      textAlign: 'center'
                    }}
                  >
                    {!loading && (
                      <S.ConditionSelect
                        value={JSON.stringify(conditionSelected)}
                        onChange={handleChangeCondition}
                        disabled={finalized}
                      >
                        <option value="">
                          Selecione a Condição de Pagamento
                        </option>

                        {conditions.map((condition) => (
                          <option
                            key={condition.cPagtoId}
                            value={JSON.stringify(condition)}
                          >
                            {`${condition.dDescricao} - VLR. MIN.: $${Number(
                              condition.vValorMin
                            ).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2
                            })}`.toLowerCase()}
                          </option>
                        ))}
                      </S.ConditionSelect>
                    )}
                  </div>

                  <div
                    style={{
                      marginTop: 10,
                      width: '100%',
                      textAlign: 'center'
                    }}
                  >
                    {!loading && (
                      <S.ConditionSelect
                        value={freightSelected}
                        onChange={handleChangeFreightType}
                        disabled={finalized}
                      >
                        <option value="">Selecione o tipo de frete</option>

                        {freightTypes.map((freightType) => (
                          <option
                            key={freightType.Codigo}
                            value={freightType.Codigo}
                          >
                            {freightType.Descricao}
                          </option>
                        ))}
                      </S.ConditionSelect>
                    )}
                  </div>

                  {errors.map((error, index) => (
                    <ErrorMessage key={index}>{error.Description}</ErrorMessage>
                  ))}

                  <div style={{ marginTop: 10 }}>
                    <S.CardButton
                      onClick={handleTransferOrder}
                      background="gray"
                      color="gray"
                      type="button"
                      disabled={
                        transferingOrders || ordersTransfer.length === 0
                      }
                    >
                      {ending ? 'Transferindo pedido...' : 'Transferir pedido'}
                    </S.CardButton>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <S.CardButton
                      onClick={handleEndRequest}
                      background="gray"
                      color="gray"
                      type="button"
                      disabled={finalized}
                    >
                      {ending ? 'Finalizando pedido...' : 'Finalizar pedido'}
                    </S.CardButton>
                  </div>
                </S.CartTotalContent>
              )}
            </S.CartTotal>
            <S.CartTotal>
              {!loading && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px 1fr',
                    background: '#FFF',
                    padding: 12,
                    borderRadius: 4,
                    gap: 10
                  }}
                >
                  {[
                    { label: 'PDV Interno', value: orders.order[0]?.cPedERP },
                    {
                      label: 'Status PDV',
                      value: orders.order[0]?.cStatusPedido
                    },
                    { label: 'Nota Fiscal', value: orders.order[0]?.cNumeroNF },
                    {
                      label: 'Chave NFE',
                      value: orders.order[0]?.cChaveNFE ? (
                        <a
                          href={`https://www.consultadanfe.com/?chave=${orders.order[0]?.cChaveNFE}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {orders.order[0]?.cChaveNFE}
                        </a>
                      ) : (
                        '-'
                      )
                    },
                    {
                      label: 'Emissão NF',
                      value: dayjs(orders.order[0]?.dtEmissaoNF).format(
                        'DD/MM/YY'
                      )
                    }
                  ].map((field, index) => (
                    <Fragment key={index}>
                      <h4>{field.label}</h4>
                      <p
                        className="wrap-text"
                        style={{
                          textAlign: 'right',
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {field.value ? field.value : '-'}
                      </p>
                    </Fragment>
                  ))}
                </div>
              )}
            </S.CartTotal>
            {banners?.find((banner) => banner.EmpresaImgPix)?.EmpresaImgPix && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 24,
                  marginBottom: 24,
                  padding: 16,
                  border: '1px dashed #ccc',
                  borderRadius: 8,
                  minHeight: 120
                }}
              >
                <img
                  alt="QR Code"
                  src={
                    banners.find((banner) => banner.EmpresaImgPix)
                      ?.EmpresaImgPix
                  }
                  style={{
                    width: '100%',
                    background: '#f5f5f5',
                    borderRadius: 8
                  }}
                />
              </div>
            )}
          </div>
        </S.CartContent>
      </Container>
    </>
  )
}
